import { useEffect, useState } from 'react'
import { Copy, ExternalLink, Loader2, Link2 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { toast } from '@/components/ui/Toast'
import { brl } from '@/lib/format'
import { PartnerService, type PartnerSalesLink } from '@/services/modules/PartnerService'

export function PartnerSalesLinkPage() {
  const [data, setData] = useState<PartnerSalesLink | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    PartnerService.salesLink().then(setData).catch(() => toast.error('Erro ao carregar o link de vendas.')).finally(() => setLoading(false))
  }, [])

  const base = window.location.origin
  const generalUrl = data?.slug ? `${base}/vendas/${data.slug}` : null

  async function copy(url: string) {
    try { await navigator.clipboard.writeText(url); toast.success('Link copiado!') }
    catch { toast.error('Não foi possível copiar.') }
  }

  return (
    <>
      <PageHeader title="Vendas" subtitle="Seu link de vendas (LP geral e por plano)"
        crumbs={[{ label: 'Links' }, { label: 'Vendas' }]} />

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 size={24} className="animate-spin text-gray-400" /></div>
      ) : !generalUrl ? (
        <div className="bg-white rounded-md ring-1 ring-gray-200 p-8 text-center text-sm text-gray-500">
          Seu link de vendas ainda não está disponível.
        </div>
      ) : (
        <div className="space-y-6">
          {/* LP geral */}
          <div className="bg-white rounded-md ring-1 ring-gray-200 p-5">
            <div className="flex items-center gap-2 mb-1"><Link2 size={16} className="text-brand-500" /><h2 className="text-sm font-semibold text-gray-800">Página geral (todos os planos)</h2></div>
            <p className="text-xs text-gray-500 mb-3">Compartilhe este link para mostrar todos os planos disponíveis.</p>
            <LinkRow url={generalUrl} onCopy={copy} />
          </div>

          {/* Por plano */}
          <div className="bg-white rounded-md ring-1 ring-gray-200 p-5">
            <h2 className="text-sm font-semibold text-gray-800 mb-1">Link por plano</h2>
            <p className="text-xs text-gray-500 mb-3">Leva direto para a contratação do plano escolhido.</p>
            {data!.plans.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">Nenhum plano publicado no site.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {data!.plans.map(p => (
                  <li key={p.id} className="py-3">
                    <div className="flex items-center justify-between gap-3 mb-1.5">
                      <span className="text-sm font-medium text-gray-800">{p.name}</span>
                      <span className="text-xs text-gray-500">{brl(p.price)}/mês</span>
                    </div>
                    <LinkRow url={`${base}/vendas/${data!.slug}/${p.id}`} onCopy={copy} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  )
}

function LinkRow({ url, onCopy }: { url: string; onCopy: (u: string) => void }) {
  return (
    <div className="flex items-center gap-2">
      <input readOnly value={url} className="flex-1 h-10 px-3 rounded-md border border-gray-300 text-sm text-gray-700 bg-gray-50" />
      <button type="button" onClick={() => onCopy(url)} className="cursor-pointer inline-flex items-center gap-1 h-10 px-3 rounded-md bg-brand-500 text-white text-sm hover:bg-brand-600">
        <Copy size={14} /> Copiar
      </button>
      <a href={url} target="_blank" rel="noreferrer" className="cursor-pointer inline-flex items-center justify-center h-10 w-10 rounded-md ring-1 ring-gray-200 text-gray-500 hover:bg-gray-50">
        <ExternalLink size={14} />
      </a>
    </div>
  )
}

export default PartnerSalesLinkPage
